import { Injectable, OnDestroy } from "@angular/core";
import { DataAccessService } from "@kells/apis/data-access";
import {
  PatientOnboardingStep,
  SubscriptionPlan,
} from "@kells/interfaces/patient";
import { BehaviorSubject, combineLatest } from "rxjs";
import { map, take } from "rxjs/operators";
import { SubSink } from "subsink";

interface OnboardingItem {
  id: keyof PatientOnboardingStep;
  icon: string;
  title: string;
  subtitle: string;
  ctaLabel: string;
  plan: SubscriptionPlan;
  planText: string;
  variant: "primary" | "link";
  completed: boolean;
}

const LOCAL_STORAGE_KEYS = {
  ITEMS: "onboarding.items",
  EXPANDED: "onboarding.expanded",
  HIDDEN: "onboarding.hidden",
};

const DEFAULT_ITEMS: OnboardingItem[] = [
  {
    id: "is_ai_virtual_checkup_done",
    icon: "assets/dental-advisor.svg",
    title: "AI Virtual Checkup",
    subtitle: "Take photos on your phone for an AI-powered checkup",
    ctaLabel: "Start Now",
    variant: "primary",
    plan: SubscriptionPlan.Basic,
    planText: "Free",
    completed: false,
  },
  {
    id: "is_view_first_report_done",
    icon: "assets/view-reports.svg",
    title: "View Your First Report",
    subtitle: "Review your oral health assessment and recommendations",
    ctaLabel: "View Report",
    variant: "link",
    plan: SubscriptionPlan.Basic,
    planText: "Free",
    completed: false,
  },
  {
    id: "is_chat_with_ai_advisor_done",
    icon: "assets/talk-to-dentist-free.svg",
    title: "Chat with AI Dental Advisor",
    subtitle:
      "Have a real-time consultation to answer your dental concerns or insurance questions",
    ctaLabel: "Chat Now",
    variant: "link",
    plan: SubscriptionPlan.Basic,
    planText: "Free",
    completed: false,
  },
  {
    id: "is_explore_all_features_done",
    icon: "assets/product-recommendation-free.svg",
    title: "Explore All Features",
    subtitle:
      "From a premiere dental network to virtual second opinion on your diagnosis, KELLS has you covered",
    ctaLabel: "Explore Premium",
    variant: "link",
    plan: SubscriptionPlan.Premium,
    planText: "Premium",
    completed: false,
  },
];

@Injectable({
  providedIn: "root",
})
export class OnboardingService implements OnDestroy {
  private _subs = new SubSink();
  private currentPatientId: string | null = null;

  private hasLS = typeof window !== "undefined" && !!window.localStorage;

  public readonly isExpanded$ = new BehaviorSubject<boolean>(true);

  public readonly isHidden$ = new BehaviorSubject<boolean>(false);

  public readonly items$ = new BehaviorSubject<OnboardingItem[]>([]);

  public readonly hiddenData$ = this.data.hiddenData$;

  public readonly onboardingSteps$ = combineLatest([
    this.data.selectedPatient$,
    this.hiddenData$,
  ]).pipe(
    map(([patient, hiddenData]) => {
      const steps = patient.onboarding_steps;

      const items = DEFAULT_ITEMS.map((item) => {
        if (item.id === "is_ai_virtual_checkup_done") {
          return {
            ...item,
            completed: steps?.is_ai_virtual_checkup_done ?? false,
          };
        }
        if (item.id === "is_view_first_report_done") {
          if (hiddenData.viewReports) return null;

          return {
            ...item,
            completed: steps?.is_view_first_report_done ?? false,
          };
        }
        if (item.id === "is_chat_with_ai_advisor_done") {
          if (hiddenData.dentalAdvisor) return null;

          return {
            ...item,
            completed: steps?.is_chat_with_ai_advisor_done ?? false,
          };
        }
        if (item.id === "is_explore_all_features_done") {
          if (hiddenData.onboarding.explorePremium) return null;

          return {
            ...item,
            completed: steps?.is_explore_all_features_done ?? false,
          };
        }

        return item;
      });

      return items.filter(Boolean) as OnboardingItem[];
    })
  );

  constructor(private readonly data: DataAccessService) {
    this._subs.sink = this.onboardingSteps$.subscribe((items) => {
      this.items$.next(items);
    });

    this._subs.sink = this.data.selectedPatient$.subscribe((patient) => {
      this.currentPatientId = patient.id;

      const expanded = this.loadPatientBool(
        LOCAL_STORAGE_KEYS.EXPANDED,
        this.currentPatientId,
        true
      );

      const hidden = this.loadPatientBool(
        LOCAL_STORAGE_KEYS.HIDDEN,
        this.currentPatientId,
        false
      );

      this.isExpanded$.next(expanded);
      this.isHidden$.next(hidden);
    });

    this._subs.sink = this.isExpanded$.subscribe((isExpanded) => {
      if (!this.hasLS || !this.currentPatientId) return;

      this.savePatientBool(
        LOCAL_STORAGE_KEYS.EXPANDED,
        this.currentPatientId,
        isExpanded
      );
    });

    this._subs.sink = this.isHidden$.subscribe((isHidden) => {
      if (!this.hasLS || !this.currentPatientId) return;

      this.savePatientBool(
        LOCAL_STORAGE_KEYS.HIDDEN,
        this.currentPatientId,
        isHidden
      );
    });
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  onCompletedChange(id: keyof PatientOnboardingStep) {
    const isCompleted = this.items$.value.find((i) => i.id === id)?.completed;

    if (isCompleted) return;

    const next = this.items$.value.map((i) =>
      i.id === id ? { ...i, completed: true } : i
    );
    this.items$.next(next);
    this.updatePatientOnboardingStep(id, next);

    const isAllCompleted = next.every((i) => i.completed);
    if (isAllCompleted) {
      this.isExpanded$.next(false);
    }
  }

  updatePatientOnboardingStep(
    step: keyof PatientOnboardingStep,
    next: OnboardingItem[]
  ): void {
    const steps = next
      .map((i) => ({ [i.id]: i.completed }))
      .reduce((a, b) => ({ ...a, ...b }), {} as PatientOnboardingStep);

    this.data
      .updatePatient({
        onboarding_steps: {
          ...steps,
          [step]: true,
        },
      })
      .pipe(take(1))
      .subscribe();
  }

  public readonly progress$ = this.items$.pipe(
    map(
      (items) =>
        (items.filter((item) => item.completed).length / items.length) * 100
    )
  );

  public isCompleted$ = this.progress$.pipe(
    map((progress) => progress === 100)
  );

  public toggle(): void {
    this.isExpanded$.next(!this.isExpanded$.value);
  }

  public hide(): void {
    this.isHidden$.next(true);
  }

  public show(): void {
    this.isHidden$.next(false);
  }

  // ---- Storage helpers ----
  private loadPatientBool(
    key: string,
    patientId: string | null,
    fallback: boolean
  ): boolean {
    if (!this.hasLS) return fallback;

    try {
      const raw = localStorage.getItem(key);
      if (raw == null) return fallback;

      const parsed = JSON.parse(raw);

      // Legacy format: single boolean shared across all patients
      if (typeof parsed === "boolean") {
        return parsed;
      }

      if (
        parsed &&
        typeof parsed === "object" &&
        !Array.isArray(parsed) &&
        patientId
      ) {
        const value = (parsed as Record<string, unknown>)[patientId];
        return typeof value === "boolean" ? value : fallback;
      }

      return fallback;
    } catch {
      return fallback;
    }
  }

  private savePatientBool(
    key: string,
    patientId: string | null,
    value: boolean
  ): void {
    if (!this.hasLS || !patientId) return;

    try {
      const raw = localStorage.getItem(key);
      let store: Record<string, unknown>;

      if (!raw) {
        store = {};
      } else {
        try {
          const parsed = JSON.parse(raw);

          if (
            parsed &&
            typeof parsed === "object" &&
            !Array.isArray(parsed)
          ) {
            store = parsed as Record<string, unknown>;
          } else if (typeof parsed === "boolean") {
            // Preserve legacy global value alongside per-patient values
            store = { __global: parsed };
          } else {
            store = {};
          }
        } catch {
          store = {};
        }
      }

      store[patientId] = value;
      localStorage.setItem(key, JSON.stringify(store));
    } catch {
      // noop
    }
  }
}
